import anime from "animejs";

const mainApiUrl = process.env.MAIN_API_URL;
const fungusApiEndpoint = `${mainApiUrl}/api`;

const queryStringForEvent = window.location.search;
const urlParamsForEvent = new URLSearchParams(queryStringForEvent);

const eventId = urlParamsForEvent.get('eventId') || 5;

/**
 * Runs every subscriber, even when one of them throws.
 *
 * These lists are fanned out from inside a `.then()`, so an unguarded throw used to skip
 * every subscriber registered after it and disappear as an unhandled rejection. Order is
 * load-bearing and nobody tracks it — the donation total is registered first and the host
 * name, leaderboard and schedule derivation last — so one bad field could silently blank
 * half the overlay while the total kept ticking and everything looked fine.
 */
export function notifyAll(subscribers, label) {
    subscribers.forEach((subscriber) => {
        try {
            subscriber();
        } catch (error) {
            console.error(`${label} subscriber "${subscriber.name || 'anonymous'}" threw:`, error);
        }
    });
}

let live = {
    offsetWriterToken: process.env.FUNGUS_API_KEY,
    eventWriteEndpoint: `${fungusApiEndpoint}`,

    apiEndpoint: fungusApiEndpoint,
    liveDataEndpoint: `${fungusApiEndpoint}/events/${eventId}/live`,
    chatDataEndpoint: `${fungusApiEndpoint}/events/${eventId}/chat`,
    adsEndpoint: `${fungusApiEndpoint}/twitch/ads`,
    shoutoutEndpoint: `${fungusApiEndpoint}/twitch/shoutout`,
    liveEventData: [],
    gufDonationData: [],
    incentives: [],

    toCallWhenReady: [],
    toCallWhenUpdated: [],
    firstInit: true,

    shouldSendAds: false,
    adSendingTimeout: null,

    fetchGufInfo: async () => {
        try {
            fetch(live.liveDataEndpoint, {
                method: "GET",
                headers: {
                    "Accept": "application/json",
                },
            }).then(async (response) => {
                const responseCode = response.status;
                if (responseCode !== 200) {
                    console.error("Failed to fetch event info:", response);
                    return;
                }

                const responseJson = await response.json();
                live.liveEventData = responseJson.data;
                if (live.firstInit) {
                    notifyAll(live.toCallWhenReady, 'callWhenReady');
                    live.firstInit = false;
                }
                notifyAll(live.toCallWhenUpdated, 'callWhenUpdated');
            });
        } catch (error) {
            console.error(error);
        }
    },

    getTotalDonationOffset: () => {
        return Number(live.liveEventData?.live?.donation_offset) || 0;
    },

    getDonationData: () => {
        return live.liveEventData?.donations ?? [];
    },

    getBidWarParents: () => {
        return live.getDonationData()?.bids?.filter(bid => bid.type === 'bidwar' && bid.parent_id === null) ?? [];
    },

    getFixedBids: () => {
        return live.getDonationData()?.bids?.filter(bid => bid.type === 'fixed') ?? [];
    },

    getBidWars: () => {
        return live.getDonationData()?.bids?.filter(bid => bid.type === 'bidwar') ?? [];
    },

    // The API serialises the total as a string ("285.00"). Without Number() the
    // offset is string-concatenated instead of added ("285.00" + 100 = "285.00100"),
    // which Intl.NumberFormat then silently renders back as 285,00 €.
    getTotalDonation: () => {
        return Number(live.getDonationData()?.total) || 0;
    },

    getTotalDonationsWithOffset: () => {
        const totalDonation = live.getTotalDonation() + live.getTotalDonationOffset();
        if (totalDonation <= 0) return 0;

        return totalDonation;
    },

    /** TOP DONATORI: each donor and their summed total, highest first. Three at most. */
    getDonatorsLeaderboard: () => {
        return live.getDonationData()?.top_donors?.map((donation) => ({
            name: donation.name,
            total: donation.amount_sum,
        })) ?? [];
    },

    getDonationLeaderboard: () => {
        let donationLeaderboard = live.getDonationData()?.top_donations?.map(donation => {
            let filteredDonations = {
                name: donation.name ?? 'Anonimo',
                total: donation.amount,
            };

            return filteredDonations;
        }) ?? [];


        return donationLeaderboard;
    },

    getInforollRows: () => {
        return live.liveEventData?.live?.inforoll_text;
    },

    // Frozen mode is set per OBS source with ?frozen=true, not by the payload.
    getFreezeInforoll: () => new URLSearchParams(location.search).get('frozen') === 'true',

    // frozen_bgm_tracks carries artist/song; frozen_bgms (bare URLs) is the fallback.
    // Uploaded "Frozen BGMs" have no metadata, so artist/song can be null.
    getFreezeInforollMedia: () => {
        const l = live.liveEventData?.live;
        const tracks = l?.frozen_bgm_tracks ?? (l?.frozen_bgms ?? []).map(url => ({ url }));
        return tracks.map(t => ({
            file_name: t.url,
            artist: t.artist || 'frozen media',
            song: t.song || 'frozen media',
        }));
    },

    getFrozenInforollText: () => {
        return live.liveEventData?.live?.frozen_inforoll_text ?? '';
    },

    getDonationReason: () => {
        return "PER " + live.liveEventData?.live?.donation_recipient ?? '';
    },

    getCurrentRun: () => {
        return live.liveEventData?.current_timeslot ?? [];
    },


    shouldShowLeaderboard: () => {
        return live.liveEventData?.live?.show_leaderboard ?? true;
    },

    getDonationGoalAmount: () => {
        return live.liveEventData?.live?.goal_amount ?? 0;
    },

    getDonationGoalFlair: () => {
        return live.liveEventData?.live?.goal_flair ?? '';
    },

    getDonationDeploySurprise: () => {
        return true;
    },

    getBreakScreenAds: () => {
        return (live.liveEventData?.live?.media ?? []);
    },

    parseIncentives: () => {
        live.incentives = [];
        live.getFixedBids()?.map(rawIncentive => {
            let incentive = [];
            incentive.type = 'fixed';
            incentive.name = rawIncentive.name;
            incentive.total = rawIncentive.amount_total;
            incentive.goal = rawIncentive.goal;
            incentive.gameName = rawIncentive.game_name ?? '';
            incentive.category = rawIncentive.game_category ?? '';
            live.incentives.push(incentive);
        })

        const optionIncentives = live.getBidWars();
        let parentIds = optionIncentives.map(i => i.parent_id) ?? [];
        let unique = [];
        parentIds.map(i => unique[i] = i);
        unique = unique.filter(Number);
        unique.map(parentId => {
            let childrenIncentive = optionIncentives.filter(o => o.parent_id == parentId);
            let incentive = [];
            let parent = live.getBidWarParents().find(p => p.id == parentId);
            incentive.type = 'option';
            incentive.name = parent.name;
            incentive.total = parent.amount_total;
            incentive.gameName = parent.game_name ?? '';
            incentive.category = parent.game_category ?? '';
            incentive.options = []
            childrenIncentive.map(childIncentive => {
                if (childIncentive.id == null) return;

                let innerIncentive = [];
                innerIncentive.id = childIncentive.id;
                const regex = /^(.*?) -.*$/ig;
                innerIncentive.name = childIncentive.name.replaceAll(regex, '$1');
                innerIncentive.total = Number(childIncentive.amount_total);
                incentive.options.push(innerIncentive);
            })
            incentive.options.sort((a, b) => b.total - a.total);
            live.incentives.push(incentive);
        })
    },

    sendAds: () => {
        const response = fetch(live.adsEndpoint, {
            method: "POST",
            headers: {
                "Authorization": `Bearer ${live.offsetWriterToken}`,
                "Content-Type": "application/json",
                "Accept": "application/json",
            },
        }).then((data) => {
            console.log("Sent ads and got back:", data);
        });
    },

    sendShoutout: () => {
        const response = fetch(live.shoutoutEndpoint, {
            method: "POST",
            headers: {
                "Authorization": `Bearer ${live.offsetWriterToken}`,
                "Content-Type": "application/json",
                "Accept": "application/json",
            },
        }).then((data) => {
            console.log("Sent shoutout and got back:", data);
        });
    },

    callWhenReady: (fn) => {
        live.toCallWhenReady.push(fn);
    },

    callWhenUpdated: (fn) => {
        live.toCallWhenUpdated.push(fn);
    },
}

export { live };
window.live = live;
// Deprecated alias: this module was called `strapi` when the backend was Strapi.
// Kept because staff type it into the devtools console on air.
window.strapi = live;
window.playBgm = false;
window.cake = false;
live.callWhenReady(function () {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);


    window.cake = urlParams.get('cake') === 'true' || false;
    if (window.cake)
        console.log("The cake is a lie.");

    live.shouldSendAds = urlParams.get('sendAds') === 'true' || false;
    if (live.shouldSendAds) {
        live.adSendingTimeout = setTimeout(live.sendAds, 10000);
        console.log("Sending ads in 10 seconds.");
    }

    const shouldSendShoutout = urlParams.get('sendShoutout') === 'true' || false;
    if (shouldSendShoutout) {
        setTimeout(function () {
            live.sendShoutout();
            console.log("Sending shoutout.");
        }, 1000 * 60 * 2);
    }

    // ?bgm=true is wired by js/audio/jukebox.js, from init.
});